import { Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { BiTranslateService } from "@globals/bi-translate";
import { BiLanguageId } from "@globals/enums/BiLanguageAndCountryId";
import { cloneObject, createAddressString, isStringNullOrEmpty, removeItemAtImmutable } from "@globals/helper-functions";
import { AddressModel } from "@globals/openapi-models/model/addressModel";
import { PhoneNumberTypeEnum } from "@globals/openapi-models/model/phoneNumberTypeEnum";
import { SubscribeAddressCommand } from "@globals/openapi-models/model/subscribeAddressCommand";
import { SubscribeSupplyNumberCommand } from "@globals/openapi-models/model/subscribeSupplyNumberCommand";
import { SubscriptionDto } from "@globals/openapi-models/model/subscriptionDto";
import { SubscriptionModuleModel } from "@globals/openapi-models/model/subscriptionModuleModel";
import { SubscriptionType } from "@globals/openapi-models/model/subscriptionType";
import { UnsubscribeAddressCommand } from "@globals/openapi-models/model/unsubscribeAddressCommand";
import { UnsubscribeSupplyNumberCommand } from "@globals/openapi-models/model/unsubscribeSupplyNumberCommand";
import { SubscribeModuleBaseService } from "@globals/services/bi-subscribe-module-base.service";
import parsePhoneNumberFromString from "libphonenumber-js";
import { BehaviorSubject, Observable, take, tap } from "rxjs";
import { SubscriptionModuleBaseSharedService } from "../shared/SubscriptionModuleBaseSharedService";
import { SupplyNumberAndAlias } from "./create-supply-number-subscription/create-supply-number-subscription.component";

@Injectable()
export class SubscriptionModuleSharedService extends SubscriptionModuleBaseSharedService {
  private subscriptions = new BehaviorSubject<SubscriptionDto[]>([]);
  public subscriptions$ = this.subscriptions.asObservable();

  private moduleConfigModelSubject = new BehaviorSubject<SubscriptionModuleModel>(undefined);

  /**
   * The config object for the subscription module based on the customer's settings.
   */
  public moduleConfigModel$ = this.moduleConfigModelSubject.asObservable();

  /**
   * The subscription overview component is destroyed each time we cange step in the subscription module. Then it's
   * impossible to know, if the first data emitted by this service is just the default (empty array) or data
   * comming from server (as we subscribe to a behaviorsubject that must have a default value).
   * This flag tells if the list of subscriptions has been initialized/loaded /and cahced) making it possible to know
   * whether to show a loading spinner or not (we shouldn't if data is already cached in frontend)
   */
  public subscriptionsInitialized = false;

  constructor(
    private subscriptionService: SubscribeModuleBaseService,
    private translator: BiTranslateService,
    protected activeRoute: ActivatedRoute
  ) {
    super();
  }

  /**
   * For getting the data for the subscription overview component
   */
  public getSubscriptionListData(onlyBusiness?: boolean, onlyPrivate?: boolean, noOperatorLookup?: boolean) {
    return this.subscriptionService
      .getSubscriptions(
        this.currentSubscriberInfo.accessToken,
        this.moduleConfigModelSubject.value.customerId,
        this.currentSubscriberInfo.mobileNumber,
        this.currentSubscriberInfo.email,
        this.currentSubscriberInfo.landlineNumber,
        onlyBusiness,
        onlyPrivate,
        noOperatorLookup
      )
      .pipe(
        take(1),
        tap(subscriptions => {
          this.subscriptionsInitialized = true;
          this.subscriptions.next(subscriptions);
        })
      );
  }

  public createAddressSubscription(subscriptionData: AddressModel): Observable<object> {
    const subscription = {
      customerId: this.moduleConfigModelSubject.value.customerId,
      kvhx: subscriptionData.kvhx,
      email: this.currentSubscriberInfo.email,
      disableLookup: !!this.urlParams.disableLookup,
      sendReceipt: !this.urlParams.disableAuth,
      name: this.currentSubscriberInfo.name,
      accessToken: this.currentSubscriberInfo.accessToken
    } as SubscribeAddressCommand;

    if (this.currentSubscriberInfo.mobileNumber) {
      const parsedPhoneNumber = parsePhoneNumberFromString(this.currentSubscriberInfo.mobileNumber);
      if (parsedPhoneNumber) {
        subscription.phoneCode = +parsedPhoneNumber.countryCallingCode;
        subscription.phoneNumber = +parsedPhoneNumber.nationalNumber;
      }
    }

    if (this.currentSubscriberInfo.landlineNumber) {
      const parsedPhoneVoice = parsePhoneNumberFromString(this.currentSubscriberInfo.landlineNumber);
      if (parsedPhoneVoice.isValid()) {
        subscription.phoneCodeVoice = +parsedPhoneVoice.countryCallingCode;
        subscription.phoneNumberVoice = +parsedPhoneVoice.nationalNumber;
      }
    }
    return this.subscriptionService.subscribeAddress(subscription).pipe(tap(() => this.cacheAddressSubscriptionData(subscriptionData)));
  }

  public unsubscribeAddress(sub: SubscriptionDto) {
    const unsubCommand = {
      customerId: sub.customerId,
      disableLookup: !!this.urlParams.disableLookup,
      kvhx: sub.identifierKey,
      sendReceipt: !this.urlParams.disableAuth,
      email: sub.email,
      accessToken: this.currentSubscriberInfo.accessToken
    } as UnsubscribeAddressCommand;

    if (sub.phoneNumberType == PhoneNumberTypeEnum.Mobile) {
      unsubCommand.phoneCode = sub.phoneCode;
      unsubCommand.phoneNumber = sub.phoneNumber;
    } else if (sub.phoneNumberType == PhoneNumberTypeEnum.LandLine) {
      unsubCommand.phoneCodeVoice = sub.phoneCode;
      unsubCommand.phoneNumberVoice = sub.phoneNumber;
    }

    return this.subscriptionService.unsubscribeAddress(unsubCommand).pipe(
      tap(() => {
        const subClone = cloneObject(this.subscriptions.getValue());

        const index = subClone.findIndex(
          x => x.identifierKey == sub.identifierKey && x.email == sub.email && x.phoneCode == sub.phoneCode && x.phoneNumber == sub.phoneNumber && x.phoneNumberType == sub.phoneNumberType
        );

        if (index > -1) {
          this.sortSubscriptions(subClone); // Be sure to re-sort the subscriptions!
          this.subscriptions.next(removeItemAtImmutable(subClone, index));
        }
      })
    );
  }

  private cacheAddressSubscriptionData(subscriptionData: AddressModel) {
    const subscriptionsClone = cloneObject(this.subscriptions.getValue());

    const locationName = createAddressString(
      this.countryId,
      subscriptionData.zipCode,
      subscriptionData.city,
      subscriptionData.street,
      subscriptionData.number,
      subscriptionData.meters,
      subscriptionData.letter,
      subscriptionData.floor,
      subscriptionData.door
    );

    const commonSubscriptionData: Partial<SubscriptionDto> = {
      customerId: this.moduleConfigModelSubject.value.customerId,
      identifierKey: subscriptionData.kvhx,
      subscriptionTypeId: SubscriptionType.Kvhx,
      locationName: locationName,
      subscriptionAddress: subscriptionData
    };

    if (this.currentSubscriberInfo.mobileNumber) {
      const parsedPhoneNumber = parsePhoneNumberFromString(this.currentSubscriberInfo.mobileNumber);
      if (parsedPhoneNumber) {
        subscriptionsClone.push({
          ...commonSubscriptionData,
          phoneCode: +parsedPhoneNumber.countryCallingCode,
          phoneNumber: +parsedPhoneNumber.nationalNumber,
          phoneNumberType: PhoneNumberTypeEnum.Mobile
        } as SubscriptionDto);
      }
    }

    if (this.currentSubscriberInfo.landlineNumber) {
      const parsedPhoneVoice = parsePhoneNumberFromString(this.currentSubscriberInfo.landlineNumber);
      if (parsedPhoneVoice.isValid()) {
        subscriptionsClone.push({
          ...commonSubscriptionData,
          phoneCode: +parsedPhoneVoice.countryCallingCode,
          phoneNumber: +parsedPhoneVoice.nationalNumber,
          phoneNumberType: PhoneNumberTypeEnum.LandLine
        } as SubscriptionDto);
      }
    }

    if (!isStringNullOrEmpty(this.currentSubscriberInfo.email)) {
      subscriptionsClone.push({
        ...commonSubscriptionData,
        email: this.currentSubscriberInfo.email
      } as SubscriptionDto);
    }

    this.sortSubscriptions(subscriptionsClone); // Be sure to re-sort the subscriptions!
    this.subscriptions.next(subscriptionsClone);
  }

  //#region Supply numbers ==========================
  public createSupplyNumberSubscription(subscriptionData: SupplyNumberAndAlias) {
    const subscription = {
      customerId: this.moduleConfigModelSubject.value.customerId,
      supplyNumber: subscriptionData.supplyNumber,
      alias: subscriptionData.alias,
      sendReceipt: !this.urlParams.disableAuth,
      email: this.currentSubscriberInfo.email,
      name: this.currentSubscriberInfo.name,
      accessToken: this.currentSubscriberInfo.accessToken
    } as SubscribeSupplyNumberCommand;

    if (this.currentSubscriberInfo.mobileNumber) {
      const parsedPhoneNumber = parsePhoneNumberFromString(this.currentSubscriberInfo.mobileNumber);
      if (parsedPhoneNumber) {
        subscription.phoneCode = +parsedPhoneNumber.countryCallingCode;
        subscription.phoneNumber = +parsedPhoneNumber.nationalNumber;
      }
    }

    if (this.currentSubscriberInfo.landlineNumber) {
      const parsedPhoneVoice = parsePhoneNumberFromString(this.currentSubscriberInfo.landlineNumber);
      if (parsedPhoneVoice.isValid()) {
        subscription.phoneCodeVoice = +parsedPhoneVoice.countryCallingCode;
        subscription.phoneNumberVoice = +parsedPhoneVoice.nationalNumber;
      }
    }
    return this.subscriptionService.subscribeSupplyNumber(subscription).pipe(tap(() => this.cacheSupplyNumberSubscriptionData(subscriptionData)));
  }

  public unsubscribeSupplyNumber(sub: SubscriptionDto) {
    const subCommand = {
      customerId: sub.customerId,
      sendReceipt: !this.urlParams.disableAuth,
      email: sub.email,
      supplyNumber: sub.identifierKey,
      accessToken: this.currentSubscriberInfo.accessToken
    } as UnsubscribeSupplyNumberCommand;

    if (sub.phoneNumberType == PhoneNumberTypeEnum.Mobile) {
      subCommand.phoneCode = sub.phoneCode;
      subCommand.phoneNumber = sub.phoneNumber;
    } else if (sub.phoneNumberType == PhoneNumberTypeEnum.LandLine) {
      subCommand.phoneCodeVoice = sub.phoneCode;
      subCommand.phoneNumberVoice = sub.phoneNumber;
    }

    return this.subscriptionService.unsubscribeSupplyNumber(subCommand).pipe(
      tap(() => {
        const subClone = cloneObject(this.subscriptions.getValue());

        const index = subClone.findIndex(
          x => x.email == sub.email && x.phoneNumberType == sub.phoneNumberType && x.phoneCode == sub.phoneCode && x.phoneNumber == sub.phoneNumber && x.identifierKey == sub.identifierKey
        );
        if (index > -1) {
          this.sortSubscriptions(subClone); // Be sure to re-sort the subscriptions!
          this.subscriptions.next(removeItemAtImmutable(subClone, index));
        }
      })
    );
  }

  private cacheSupplyNumberSubscriptionData(subscriptionData: SupplyNumberAndAlias) {
    const subscriptionsClone = cloneObject(this.subscriptions.getValue());

    const commonSubscriptionData: Partial<SubscriptionDto> = {
      customerId: this.moduleConfigModelSubject.value.customerId,
      identifierKey: subscriptionData.supplyNumber,
      locationName: this.getSupplyNumberName(subscriptionData.supplyNumber, subscriptionData.alias),
      subscriptionTypeId: SubscriptionType.SupplyNumber,
      subscriberName: this.currentSubscriberInfo.name
    };

    if (this.currentSubscriberInfo.mobileNumber) {
      const parsedPhoneNumber = parsePhoneNumberFromString(this.currentSubscriberInfo.mobileNumber);
      if (parsedPhoneNumber) {
        subscriptionsClone.push({
          ...commonSubscriptionData,
          phoneCode: +parsedPhoneNumber.countryCallingCode,
          phoneNumber: +parsedPhoneNumber.nationalNumber,
          phoneNumberType: PhoneNumberTypeEnum.Mobile
        } as SubscriptionDto);
      }
    }

    if (this.currentSubscriberInfo.landlineNumber) {
      const parsedPhoneVoice = parsePhoneNumberFromString(this.currentSubscriberInfo.landlineNumber);
      if (parsedPhoneVoice.isValid()) {
        subscriptionsClone.push({
          ...commonSubscriptionData,
          phoneCode: +parsedPhoneVoice.countryCallingCode,
          phoneNumber: +parsedPhoneVoice.nationalNumber,
          phoneNumberType: PhoneNumberTypeEnum.LandLine
        } as SubscriptionDto);
      }
    }
    if (!isStringNullOrEmpty(this.currentSubscriberInfo.email)) {
      subscriptionsClone.push({
        ...commonSubscriptionData,
        email: this.currentSubscriberInfo.email
      } as SubscriptionDto);
    }

    this.sortSubscriptions(subscriptionsClone); // Be sure to re-sort the subscriptions!
    this.subscriptions.next(subscriptionsClone);
  }

  public getSupplyNumberName(supplyNumber: string, supplyNumberAlias: string): string {
    const supplyNumberString = !isStringNullOrEmpty(this.moduleConfigModelSubject.value.supplyNumberText)
      ? this.moduleConfigModelSubject.value.supplyNumberText
      : this.translator.instant("shared.SupplyNumber");

    return isStringNullOrEmpty(supplyNumberAlias) ? supplyNumberString + " " + supplyNumber : `${supplyNumberAlias} (${supplyNumber})`;
  }
  //#endregion =======================================

  /**
   * Sorts subscriptions - ONLY relevant for subscriptions with address data!
   */
  public sortSubscriptions(subs: Array<SubscriptionDto>) {
    subs.sort((s1, s2) => {
      if (s1.subscriptionTypeId !== SubscriptionType.SupplyNumber && s2.subscriptionTypeId !== SubscriptionType.SupplyNumber) {
        // first sort by zip asc...
        if (s1.subscriptionAddress.zipcode < s2.subscriptionAddress.zipcode) return -1;
        if (s1.subscriptionAddress.zipcode > s2.subscriptionAddress.zipcode) return 1;

        // same zip. sort by street (aplhabetically)
        const street1 = s1.subscriptionAddress.street.toLowerCase(),
          street2 = s2.subscriptionAddress.street.toLowerCase();
        if (street1 < street2) return -1;
        if (street1 > street2) return 1;

        // same street. Sort by house number
        if (s1.subscriptionAddress.number < s2.subscriptionAddress.number) return -1;
        if (s1.subscriptionAddress.number > s2.subscriptionAddress.number) return 1;

        // same house number. now check if there's letter and sort by that
        if (s1.subscriptionAddress.letter || s2.subscriptionAddress.letter) {
          if (s1.subscriptionAddress.letter && s2.subscriptionAddress.letter) {
            if (s1.subscriptionAddress.letter < s2.subscriptionAddress.letter) return -1;
            if (s1.subscriptionAddress.letter > s2.subscriptionAddress.letter) return 1;
          } else {
            return !s1.subscriptionAddress.letter ? -1 : 1; // the one without letter comes first
          }
        }

        // Either same letter or no letter at all. Sort by floor (both should have floor if floor exists)
        if (s1.subscriptionAddress.floor && s2.subscriptionAddress.floor) {
          // "kl" and "st" must be set to numbers so we can sort correctly
          if (s1.subscriptionAddress.floor === "kl") s1.subscriptionAddress.floor = "-1";
          if (s1.subscriptionAddress.floor === "st") s1.subscriptionAddress.floor = "0";

          if (Number(s1.subscriptionAddress.floor) < Number(s2.subscriptionAddress.floor)) return -1;
          if (Number(s1.subscriptionAddress.floor) > Number(s2.subscriptionAddress.floor)) return 1;
        }

        // Same floor. Sort by door if there
        if (s1.subscriptionAddress.door && s2.subscriptionAddress.door) {
          if (s1.subscriptionAddress.door < s2.subscriptionAddress.door) return -1;
          if (s1.subscriptionAddress.letter > s2.subscriptionAddress.letter) return 1;
        }
      } else {
        if (s1.locationName > s2.locationName) return -1;
        if (s1.locationName < s2.locationName) return 1;
      }
      // If we've reached this point, it's the same address and there must be both an email and a phone number
      // associated with it. Make the one being the phone number subscription come first
      return s1.smsSubscription ? -1 : 1;
    });
  }

  /**
   * Go get a customer's configurations for the subscription module. This will also set the language used for translations.
   * @param publicId The customer's Public Id
   */
  public initModuleConfigObject(publicId: string, languageId?: BiLanguageId): Observable<SubscriptionModuleModel> {
    return this.subscriptionService.getSubscriptionModuleModel(publicId, languageId).pipe(
      take(1),
      tap(moduleConfig => {
        this.countryId = moduleConfig.countryId;
        this.customerId = moduleConfig.customerId;
        this.disclaimerText = moduleConfig.disclaimer;
        this.allowOnlySingleContactInfo = moduleConfig.allowOnlySingleContactInfo;
        this.moduleConfigModelSubject.next(moduleConfig);
      })
    );
  }

  public refreshData() {
    return this.getSubscriptionListData();
  }

  public getCachedModuleConfigObject() {
    return this.moduleConfigModelSubject.value;
  }
}
